/**
 * usePortfolios — CRUD for the signed-in user's saved portfolios.
 *
 * The server always scopes by the session owner, so no user id is ever sent.
 * A duplicate name comes back as 409 `name_exists`; the caller asks the user
 * and replays with `overwrite: true`.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function usePortfolios() {
  const { isActive } = useAuth();
  const [portfolios, setPortfolios] = useState([]);
  const [maxPortfolios, setMaxPortfolios] = useState(10);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!isActive) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/portfolios`);
      setPortfolios(data.portfolios || []);
      setMaxPortfolios(data.max_portfolios ?? 10);
    } catch {
      /* non-fatal: the panel shows an empty list */
    } finally {
      setLoading(false);
    }
  }, [isActive]);

  useEffect(() => { refresh(); }, [refresh]);

  const save = useCallback(async ({ name, items, overwrite = false, sessionId = null }) => {
    const { data } = await axios.post(`${API}/portfolios`, {
      name, items, overwrite, session_id: sessionId,
    });
    await refresh();
    return data;
  }, [refresh]);

  /** Reopen the analysis stored with a portfolio (404/410 when there is none). */
  const restoreAnalysis = useCallback(async (portfolioId) => {
    const { data } = await axios.post(
      `${API}/portfolios/${portfolioId}/analysis/restore`,
    );
    return data;
  }, []);

  /** Which ETFs changed their holdings since that analysis. */
  const fetchAnalysisDiff = useCallback(async (portfolioId, { detail = true } = {}) => {
    const { data } = await axios.get(
      `${API}/portfolios/${portfolioId}/analysis/diff`,
      { params: { detail } },
    );
    return data;
  }, []);

  const rename = useCallback(async (portfolioId, name) => {
    const { data } = await axios.patch(
      `${API}/portfolios/${portfolioId}`, { name },
    );
    await refresh();
    return data;
  }, [refresh]);

  const remove = useCallback(async (portfolioId) => {
    await axios.delete(`${API}/portfolios/${portfolioId}`);
    await refresh();
  }, [refresh]);

  return {
    portfolios, maxPortfolios, loading, refresh, save, rename, remove,
    restoreAnalysis, fetchAnalysisDiff,
  };
}
